/**
 * Shown at the top of every page when the logged-in user is not yet
 * email- or phone-verified.
 *
 * Two modes:
 *   - Missing email  → inline form "Add your email" + submit which POSTs
 *     to /api/me/set-email (server stores + triggers a verification link).
 *   - Has email       → informational "we sent a link" line + Resend.
 */
import { useState } from "react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Mail, X } from "lucide-react";

export default function VerificationBanner() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [email, setEmail] = useState("");
  const [pendingEmail, setPendingEmail] = useState("");   // after we submit, show "sent to X" without a full refresh

  if (!user || dismissed) return null;
  if (user.email_verified || user.phone_verified || user.is_admin || user.is_super_admin) return null;

  const hasEmail = !!(user.email || pendingEmail);

  const resend = async () => {
    const target = pendingEmail || user.email;
    if (!target) return;
    setBusy(true);
    try {
      await http.post("/auth/resend-verification", { email: target });
      toast.success("Verification email sent — check your inbox.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    } finally { setBusy(false); }
  };

  const submitAddEmail = async (e) => {
    e.preventDefault();
    const clean = (email || "").trim().toLowerCase();
    if (!/^\S+@\S+\.\S+$/.test(clean)) return toast.error("Please enter a valid email address.");
    setBusy(true);
    try {
      await http.post("/me/set-email", { email: clean });
      setPendingEmail(clean);
      setEmail("");
      toast.success("Verification link sent — check your inbox.");
      try { await refresh?.(); } catch { /* noop */ }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add email. Try again.");
    } finally { setBusy(false); }
  };

  return (
    <div
      data-testid="verify-banner"
      className="relative flex flex-wrap items-center justify-center gap-3 py-2.5 px-4 text-sm border-b"
      style={{ background: "#FEF3C7", borderColor: "#F59E0B", color: "#78350F" }}
    >
      <Mail className="w-4 h-4 shrink-0" />
      {hasEmail ? (
        <>
          <span>
            Verify your email to unlock posting, family trees, and comments. We sent a link to <b>{pendingEmail || user.email}</b>.
          </span>
          <button
            onClick={resend}
            disabled={busy}
            data-testid="verify-banner-resend"
            className="underline font-medium hover:opacity-80"
          >
            {busy ? "Sending…" : "Resend email"}
          </button>
        </>
      ) : (
        <>
          <span className="mr-1">Add your email to unlock posting, family trees, and comments:</span>
          <form onSubmit={submitAddEmail} className="inline-flex flex-wrap items-center gap-2">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="px-3 py-1 rounded-md border text-sm bg-white"
              style={{ borderColor: "#F59E0B", minWidth: 220 }}
              data-testid="verify-banner-email-input"
              disabled={busy}
            />
            <button
              type="submit"
              disabled={busy || !email}
              className="px-3 py-1 rounded-md font-medium text-white text-sm disabled:opacity-60"
              style={{ background: "#B25D38" }}
              data-testid="verify-banner-email-submit"
            >
              {busy ? "Sending…" : "Send verification link"}
            </button>
          </form>
        </>
      )}
      <button
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        className="opacity-70 hover:opacity-100 ml-2"
        data-testid="verify-banner-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
